/* =====================================================
   BULK ORDER PARSER + PRODUCT MATCHING
   ===================================================== */

/* =======================
   BASIC PARSING HELPERS
   ======================= */

// normalize spaces
function normalizeSpaces(str) {
    return str.replace(/\s+/g, " ").trim();
}

// parse a single product line
function parseProductLine(line) {
    const clean = normalizeSpaces(line);

    // 1️⃣ Multiplier at END like: 3x12 or 2x40 or 3x1,5
    const multiplierMatch = clean.match(/(\d+)[xX](\d+(?:[.,]\d+)?)$/);

    if (multiplierMatch) {
        const multiplier = Number(multiplierMatch[1]);
        const unitSize = Number(multiplierMatch[2].replace(",", "."));
        const name = clean.slice(0, multiplierMatch.index).trim();

        return {
            raw: line,
            name,
            quantity: multiplier * unitSize,
            multiplier,
            unitSize,
            quantityExplicit: true
        };
    }

    // 2️⃣ Fallback: last standalone number at END as quantity
    const quantityMatch = clean.match(/(\d+)\s*$/);

    let quantity = 1;
    let name = clean;
    let quantityExplicit = false;

    if (quantityMatch) {
        quantity = Number(quantityMatch[1]);
        name = clean.slice(0, quantityMatch.index).trim();
        quantityExplicit = true;
    }

    return {
        raw: line,
        name,
        quantity,
        quantityExplicit
    };
}

/* =======================
   MAIN TEXT PARSER
   ======================= */

export function parseBulkOrders(rawText) {
    if (!rawText) return [];

    const lines = rawText
        .split("\n")
        .map(l => l.trim())
        .filter(Boolean);

    const result = [];
    let currentClient = null;

    lines.forEach(line => {
        // CLIENT line
        if (line.toLowerCase().startsWith("cde")) {
            currentClient = { client: line, products: [] };
            result.push(currentClient);
            return;
        }

        if (currentClient) {
            const productLine = line.startsWith("-") ? line.slice(1).trim() : line;
            const parsed = parseProductLine(productLine);

            // Add precomputed tokens for better matching
            parsed.tokens = tokenize(parsed.name);

            currentClient.products.push(parsed);
        }
    });

    return result;
}



/* ======================================================================================*/
/* =======================
   MATCHING HELPERS
   ======================= */

function normalize(text = "") {
    return text
        .replace(/[\u200E\u200F\u202A-\u202E\uFEFF]/g, "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function extractSize(text) {
    const m = normalize(text).match(/\b(\d+)\s?(g|kg)\b/);
    if (!m) return null;

    let value = parseInt(m[1], 10);
    if (m[2] === "kg") value *= 1000;
    return value;
}

const STOP_WORDS = ["kg", "g", "x", "pc"];

function tokenize(text) {
    const words = normalize(text)
        .split(" ")
        .filter(w => w.length > 2 && !STOP_WORDS.includes(w));

    return [...new Set(words)]; // remove initials
}


/* =======================
   SMART MATCHER
   ======================= */

export function smartMatchProduct(rawName, allProducts, manualOverrides = {}) {
    const cleanRaw = rawName.trim().toLowerCase();

    // 1. Check Manual Overrides first
    const overrideId = manualOverrides[rawName];
    if (overrideId) {
        return {
            product: allProducts.find(p => p.id === overrideId) || null,
            confidence: 1,
            matchedBy: "manual",
        };
    }

    // 2. 🔥 NEW: Strict Exact String Match
    // If the user pasted the exact name from the DB, return 'exact' immediately
    const exactMatch = allProducts.find(p => p.name.trim().toLowerCase() === cleanRaw);
    if (exactMatch) {
        return {
            product: exactMatch,
            confidence: 1,
            matchedBy: "exact", // This ensures Green UI
        };
    }

    // 3. Tokenize for scoring (Fuzzy logic)
    const inputTokens = tokenize(rawName);
    if (inputTokens.length === 0) return { product: null, confidence: 0, matchedBy: "none" };

    let best = null;
    let bestScore = 0;
    let bestPartialMatches = 0;

    for (const p of allProducts) {
        const productTokens = tokenize(p.name);
        const productClean = p.name.trim().toLowerCase();

        // Extra safety: if tokens didn't catch it but string includes it
        if (productClean.includes(cleanRaw) || cleanRaw.includes(productClean)) {
            // Give a high boost for containment
            bestScore = 100;
            best = p;
            break;
        }

        let score = 0;
        let partialMatches = 0;

        inputTokens.forEach(t => {
            if (productTokens.includes(t)) {
                score += 2;
                partialMatches++;
            } else if (productTokens.some(pt => pt.startsWith(t))) {
                score += 1;
                partialMatches++;
            }
        });

        if (score > bestScore) {
            bestScore = score;
            best = p;
            bestPartialMatches = partialMatches;
        }
    }

    if (!best || bestScore <= 0)
        return { product: null, confidence: 0, matchedBy: "none" };

    // If we hit our "Containment/Exact" boost
    if (bestScore >= 100) {
        return { product: best, confidence: 1, matchedBy: "exact" };
    }

    const overlapRatio = inputTokens.length ? bestPartialMatches / inputTokens.length : 0;

    // 4. Determine final status
    if (overlapRatio === 1) {
        return { product: best, confidence: 1, matchedBy: "exact" };
    }

    if (overlapRatio >= 0.6 && bestScore >= 2)
        return { product: best, confidence: 0.7, matchedBy: "fuzzy" };

    if (inputTokens.length === 1 && bestScore === 2)
        return { product: best, confidence: 0.6, matchedBy: "partial" };

    if (bestScore < 3)
        return { product: null, confidence: 0, matchedBy: "none" };

    return {
        product: best,
        confidence: Math.min(1, bestScore / (inputTokens.length * 2)),
        matchedBy: "fuzzy",
    };
}

/* =======================
   MATCH WHOLE ORDERS
   ======================= */

export function matchParsedOrders(productsArray, allProducts, manualOverrides) {
    // 🛡️ Safety check: ensure we have an array
    if (!Array.isArray(productsArray)) return [];

    return productsArray.map(p => {
        const match = smartMatchProduct(
            p.name,
            allProducts,
            manualOverrides
        );

        let finalQuantity = p.quantity;

        // ✅ Apply Cxx default logic
        if (
            match.product &&
            !p.quantityExplicit &&
            p.quantity === 1
        ) {
            const cxxMatch = match.product.name.match(/\bC(\d+)\b/i);
            if (cxxMatch) {
                finalQuantity = parseInt(cxxMatch[1], 10);
            }
        }

        return {
            ...p,
            quantity: finalQuantity,
            matchedProduct: match.product,
            confidence: match.confidence,
            matchedBy: match.matchedBy,
        };
    });
}
/* =======================
   UI HELPERS
   ======================= */

export function getMatchColor(p) {
    if (p.matchedBy === "manual" || p.matchedBy === "exact") return "green";
    if (p.matchedBy === "fuzzy" || p.matchedBy === "partial") return "orange";
    return "red";
}
